import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import { all_routes } from "../../Router/all_routes";
import { api } from "../../api/client";

const PURCHASE_TAX_BASE = "/api/tax-report";

const toCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const getLocalizedText = (value, fallback = "N/A") => {
  if (typeof value === "string") return value || fallback;
  if (value && typeof value === "object") {
    return value.en || value.ar || Object.values(value)[0] || fallback;
  }
  return fallback;
};

const formatDateForApi = (dateObj) => {
  if (!dateObj) return "";
  const parsed = dayjs(dateObj);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const buildReportParams = (filters = {}, extra = {}) => {
  const params = {
    sortBy: filters?.sortBy || "purchaseDate",
    sortOrder: filters?.sortOrder || "desc",
    ...extra,
  };

  const date = formatDateForApi(filters?.date);
  const from = formatDateForApi(filters?.from);
  const to = formatDateForApi(filters?.to);

  if (date) {
    params.date = date;
  } else {
    if (from) params.from = from;
    if (to) params.to = to;
  }

  if (filters?.warehouseId) params.warehouseId = filters.warehouseId;
  if (filters?.supplierId) params.supplierId = filters.supplierId;
  if (filters?.paymentStatus) params.paymentStatus = filters.paymentStatus;

  const search = String(filters?.search || "").trim();
  if (search) params.search = search;

  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
};

const mapReportRow = (row = {}, index = 0) => {
  const rawDate = row?.date || row?.purchaseDate || row?.createdAt || "";
  const formattedDate = dayjs(rawDate).isValid()
    ? dayjs(rawDate).format("DD/MM/YYYY")
    : "-";

  return {
    id: row?._id || row?.id || `${row?.reference || "purchase-tax"}-${index}`,
    reference: row?.reference || "-",
    supplier: getLocalizedText(row?.supplier, "-"),
    date: formattedDate,
    warehouse: getLocalizedText(row?.warehouse || row?.store, "-"),
    amount: Number(row?.amount || 0),
    paymentStatus: row?.paymentStatus || row?.paymentMethod || "-",
    discount: Number(row?.discount || 0),
    taxAmount: Number(row?.taxAmount || 0),
    paidAmount: Number(row?.paidAmount || 0),
    unpaidAmount: Number(row?.unpaidAmount || 0),
  };
};

const TaxReport = () => {
  const t = (text) => (window.translate ? window.translate(text) : text);
  const route = all_routes;

  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    totalOverdue: 0,
    totalTaxAmount: 0,
    totalDiscountAmount: 0,
    totalTransactions: 0,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const [warehouseOptions, setWarehouseOptions] = useState([
    { value: "", label: t("All Warehouses") },
  ]);
  const [supplierOptions, setSupplierOptions] = useState([
    { value: "", label: t("All Suppliers") },
  ]);

  const paymentStatusOptions = useMemo(
    () => [
      { value: "", label: t("All Payment Status") },
      { value: "Paid", label: t("Paid") },
      { value: "Unpaid", label: t("Unpaid") },
      { value: "Overdue", label: t("Overdue") },
      { value: "Partial", label: t("Partial") },
    ],
    [],
  );

  const [filters, setFilters] = useState({
    date: null,
    from: null,
    to: null,
    warehouseId: "",
    supplierId: "",
    paymentStatus: "",
    search: "",
    sortBy: "purchaseDate",
    sortOrder: "desc",
  });
  const [submittedFilters, setSubmittedFilters] = useState({
    date: null,
    from: null,
    to: null,
    warehouseId: "",
    supplierId: "",
    paymentStatus: "",
    search: "",
    sortBy: "purchaseDate",
    sortOrder: "desc",
  });

  const loadFilterOptions = async () => {
    try {
      setFiltersLoading(true);
      const response = await api.get(`${PURCHASE_TAX_BASE}/purchase-filters`);
      const payload = response?.data?.data || response?.data || {};

      const warehouses = Array.isArray(payload?.warehouses)
        ? payload.warehouses
        : [];
      const suppliers = Array.isArray(payload?.suppliers) ? payload.suppliers : [];

      setWarehouseOptions([
        { value: "", label: t("All Warehouses") },
        ...warehouses.map((warehouse) => ({
          value: String(warehouse?._id || ""),
          label: getLocalizedText(
            warehouse?.warehouseName || warehouse?.name,
            t("Warehouse"),
          ),
        })),
      ]);
      setSupplierOptions([
        { value: "", label: t("All Suppliers") },
        ...suppliers.map((supplier) => ({
          value: String(supplier?._id || ""),
          label: getLocalizedText(supplier?.name, t("Supplier")),
        })),
      ]);
    } catch (error) {
      message.error(
        error?.message || t("Failed to load purchase tax report filters"),
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchPurchaseTaxReport = async (
    activeFilters = submittedFilters,
    page = pagination.page,
    limit = pagination.limit,
  ) => {
    try {
      setLoading(true);

      const listParams = buildReportParams(activeFilters, { page, limit });
      const summaryParams = buildReportParams(activeFilters);

      const [listResponse, summaryResponse] = await Promise.all([
        api.get(`${PURCHASE_TAX_BASE}/purchase-list`, { params: listParams }),
        api.get(`${PURCHASE_TAX_BASE}/purchase-summary`, {
          params: summaryParams,
        }),
      ]);

      const listPayload = listResponse?.data || {};
      const summaryPayload =
        summaryResponse?.data?.data ||
        summaryResponse?.data ||
        listPayload?.summary ||
        {};

      const rawRows = Array.isArray(listPayload?.data) ? listPayload.data : [];
      setTableRows(rawRows.map(mapReportRow));

      setSummary({
        totalAmount: Number(summaryPayload?.totalAmount || 0),
        totalPaid: Number(summaryPayload?.totalPaid || 0),
        totalUnpaid: Number(summaryPayload?.totalUnpaid || 0),
        totalOverdue: Number(summaryPayload?.totalOverdue || 0),
        totalTaxAmount: Number(summaryPayload?.totalTaxAmount || 0),
        totalDiscountAmount: Number(summaryPayload?.totalDiscountAmount || 0),
        totalTransactions: Number(summaryPayload?.totalTransactions || 0),
      });

      const listPagination = listPayload?.pagination || {};
      setPagination({
        total: Number(listPagination?.total || rawRows.length || 0),
        page: Number(listPagination?.page || page || 1),
        limit: Number(listPagination?.limit || limit || 10),
        totalPages: Number(listPagination?.totalPages || 1),
      });
    } catch (error) {
      setTableRows([]);
      setSummary({
        totalAmount: 0,
        totalPaid: 0,
        totalUnpaid: 0,
        totalOverdue: 0,
        totalTaxAmount: 0,
        totalDiscountAmount: 0,
        totalTransactions: 0,
      });
      setPagination({
        total: 0,
        page: 1,
        limit,
        totalPages: 1,
      });
      message.error(error?.message || t("Failed to fetch purchase tax report"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilterOptions();
    fetchPurchaseTaxReport(submittedFilters, 1, pagination.limit);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateReport = async (event) => {
    event.preventDefault();
    setSubmittedFilters(filters);
    await fetchPurchaseTaxReport(filters, 1, pagination.limit);
  };

  const handleRefresh = async () => {
    await fetchPurchaseTaxReport(submittedFilters, pagination.page, pagination.limit);
  };

  const handlePageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage > Number(pagination.totalPages || 1)) return;
    await fetchPurchaseTaxReport(submittedFilters, nextPage, pagination.limit);
  };

  const handleLimitChange = async (event) => {
    const nextLimit = Number(event.target.value || 10);
    if (!Number.isFinite(nextLimit) || nextLimit <= 0) return;
    await fetchPurchaseTaxReport(submittedFilters, 1, nextLimit);
  };

  const columns = [
    {
      title: t("Reference"),
      dataIndex: "reference",
      render: (text) => (
        <Link to="#" className="text-orange">
          {text}
        </Link>
      ),
      sorter: (a, b) =>
        String(a.reference || "").localeCompare(String(b.reference || "")),
    },
    {
      title: t("Supplier"),
      dataIndex: "supplier",
      sorter: (a, b) =>
        String(a.supplier || "").localeCompare(String(b.supplier || "")),
    },
    {
      title: t("Date"),
      dataIndex: "date",
      sorter: (a, b) => String(a.date || "").localeCompare(String(b.date || "")),
    },
    {
      title: t("Warehouse"),
      dataIndex: "warehouse",
      sorter: (a, b) =>
        String(a.warehouse || "").localeCompare(String(b.warehouse || "")),
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
    },
    {
      title: t("Payment Status"),
      dataIndex: "paymentStatus",
      sorter: (a, b) =>
        String(a.paymentStatus || "").localeCompare(String(b.paymentStatus || "")),
    },
    {
      title: t("Discount"),
      dataIndex: "discount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.discount || 0) - Number(b.discount || 0),
    },
    {
      title: t("Tax Amount"),
      dataIndex: "taxAmount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.taxAmount || 0) - Number(b.taxAmount || 0),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="table-tab">
          <ul className="nav nav-pills">
            <li className="nav-item">
              <Link className="nav-link active" to={route.taxreport}>
                {t("Purchase Tax")}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={route.salereport}>
                {t("Sales Tax")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{t("Purchase Tax Report")}</h4>
              <h6>{t("View Reports of Purchase Tax")}</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <Link to="#" onClick={handleRefresh}>
                <RefreshIcon />
              </Link>
            </li>
            <CollapesIcon />
          </ul>
        </div>

        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-success sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-success text-white">
                  <i className="ti ti-align-box-bottom-left-filled fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Total Amount")}</p>
                  <h3>{toCurrency(summary.totalAmount)}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-info sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-info text-white">
                  <i className="ti ti-wallet fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Total Paid")}</p>
                  <h3>{toCurrency(summary.totalPaid)}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-orange sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-orange text-white">
                  <i className="ti ti-moneybag fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Total Unpaid")}</p>
                  <h3>{toCurrency(summary.totalUnpaid)}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-danger sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-danger text-white">
                  <i className="ti ti-alert-circle-filled fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Tax Amount")}</p>
                  <h3>{toCurrency(summary.totalTaxAmount)}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0">
          <div className="card-body pb-1">
            <form onSubmit={handleGenerateReport}>
              <div className="row align-items-end">
                <div className="col-lg-10">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={t("dd/mm/yyyy")}
                            value={filters.date ? dayjs(filters.date) : null}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                date: value ? value.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("From Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={t("dd/mm/yyyy")}
                            value={filters.from ? dayjs(filters.from) : null}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                from: value ? value.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("To Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={t("dd/mm/yyyy")}
                            value={filters.to ? dayjs(filters.to) : null}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                to: value ? value.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Warehouse")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={warehouseOptions}
                          isLoading={filtersLoading}
                          placeholder={t("Choose")}
                          value={
                            warehouseOptions.find(
                              (option) => option.value === filters.warehouseId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              warehouseId: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Supplier")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={supplierOptions}
                          isLoading={filtersLoading}
                          placeholder={t("Choose")}
                          value={
                            supplierOptions.find(
                              (option) => option.value === filters.supplierId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              supplierId: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Payment Status")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={paymentStatusOptions}
                          placeholder={t("Choose")}
                          value={
                            paymentStatusOptions.find(
                              (option) => option.value === filters.paymentStatus,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              paymentStatus: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">{t("Search")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("Search by purchase reference")}
                          value={filters.search}
                          onChange={(event) =>
                            setFilters((prev) => ({
                              ...prev,
                              search: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2">
                  <div className="mb-3">
                    <button className="btn btn-primary w-100" type="submit">
                      {t("Generate Report")}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="card table-list-card no-search">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div>
              <h4>{t("Purchase Tax Report")}</h4>
              <small className="text-muted">
                {t("Total")}: {pagination.total} | {t("Page")} {pagination.page}/
                {pagination.totalPages}
              </small>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={t("Print")}
                >
                  <i className="ti ti-printer" />
                </Link>
              </li>
            </ul>
          </div>
          <div className="card-body">
            <div className="table-responsive custome-search">
              <Table columns={columns} dataSource={tableRows} loading={loading} />
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0">{t("Rows")}</label>
                <select
                  className="form-select form-select-sm"
                  value={pagination.limit}
                  onChange={handleLimitChange}
                  style={{ width: 90 }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={loading || pagination.page <= 1}
                >
                  {t("Previous")}
                </button>
                <span className="fw-medium">
                  {t("Page")} {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={loading || pagination.page >= pagination.totalPages}
                >
                  {t("Next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommonFooter />
    </div>
  );
};

export default TaxReport;
